<template>
    <div class="space-y-4">
        <!-- Pricing Section -->
        <div class="rounded-lg bg-[#F0F0F0] px-3 py-2">
            <!-- Price Row -->
            <div v-if="userStore.hasPriceViewerPermission" class="text-[#171717] text-sm flex items-center gap-1">
                <span>{{ t('propertyReport.form.pricePerReport') }}</span>
                <span v-if="formStore.pricingLoading" class="inline-flex items-center px-2">
                    <Loader class="!h-3 !w-3" />
                </span>
                <span v-else-if="formStore.pricePerReport >= 0" class="font-semibold">${{ formStore.formatPrice(formStore.pricePerReport) }}</span>
                <span v-else class="text-[#AF1E2C]">{{ t('propertyReport.form.priceNotAvailable') }}</span>
            </div>
            <div v-else class="text-[#464646] text-sm">
                {{ t('propertyReport.form.priceWillBeAssignedAtBranch') }}
            </div>
            <!-- Free Trials Row -->
            <div v-if="formStore.freeReportsRemaining > 0" class="flex items-center gap-2">
                <span class="text-sm text-[#171717]">{{ t('propertyReport.form.freeTrialReportsRemaining') }}</span>
                <span v-if="formStore.freeTrialsLoading"
                    class="inline-flex items-center px-2">
                    <Loader class="!h-3 !w-3" />
                </span>
                <span v-else
                    class="inline-flex items-center justify-center min-w-[30px] h-6 px-2 bg-[#F7C053] text-[#69470C] text-sm font-semibold rounded-full">{{
                    formStore.freeReportsRemaining }}</span>
            </div>
        </div>

        <!-- Branch / Job Account Selection -->
        <div class="border-y border-[#D1D1D1] py-3 overflow-visible">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 overflow-visible">
                <div class="flex items-center gap-1 px-2 rounded transition-shadow duration-200"
                    :class="isBranchDropdownOpen ? 'shadow-[0px_0px_5.5px_4px_#4A7FFFB2]' : ''">
                    <label class="text-sm text-[#171717] mb-0">{{ t('propertyReport.form.branch') }}: </label>
                    <NewPropertyReportFormBranchSelect v-model="formStore.selectedBranchCode" :branches="formStore.branches"
                        :placeholder="t('propertyReport.form.selectBranch', 'Select Branch')"
                        @update:modelValue="onBranchChange" @toggle="onBranchDropdownToggle" @open="isBranchDropdownOpen = true" @close="isBranchDropdownOpen = false" />
                </div>
                <div class="flex items-center gap-1 px-2 mr-2 rounded transition-shadow duration-200"
                    :class="isJobAccountDropdownOpen ? 'shadow-[0px_0px_5.5px_4px_#4A7FFFB2]' : ''">
                    <label class="text-sm text-[#171717] mb-0">{{
                        t('propertyReport.form.jobAccount') }}: </label>
                    <div class="job-account-select-comp relative">
                        <!-- Selected Value -->
                        <div class="flex items-center cursor-pointer py-1"
                            @click="toggleJobAccountDropdown()">
                            <span class="text-[#171717] text-sm font-semibold truncate">{{
                                formStore.selectedJobAccountLabel
                                }}</span>
                            <i class="text-[#171717] text-xl ml-0.5 mt-0.5 font-semibold flex-shrink-0"
                                :class="isJobAccountDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                        </div>

                        <!-- Dropdown List -->
                        <div v-if="isJobAccountDropdownOpen"
                            class="absolute -left-[180px] mt-2 bg-[#fff] border border-[#D1D1D1] rounded shadow-md z-50 max-h-80 overflow-y-auto min-w-[280px]">
                            <!-- Selected Job Account Section -->
                            <div v-if="formStore.selectedShipToSequence">
                                <div
                                    class="px-3 py-2 text-sm text-[#6E6E6E]">
                                    Selected Job Account
                                </div>
                                <div v-for="s in formStore.shipToAddresses.filter(a => String(a.ShipToSequenceId) === String(formStore.selectedShipToSequence))"
                                    :key="'selected-' + s.ShipToSequenceId"
                                    class="flex justify-between items-center px-3 py-2.5 text-sm bg-[#F4EAEA]">
                                    <span class="truncate w-full text-[#464646]">#{{ s.ShipToSequenceId }}: {{
                                        s.AddressLine2 }}</span>
                                </div>
                            </div>

                            <!-- Available Job Accounts Section -->
                            <div v-if="formStore.availableJobAccounts.length > 0">
                                <div
                                    class="px-3 py-2 text-sm text-[#6E6E6E]">
                                    Available Job Accounts
                                </div>
                                <div v-for="s in formStore.availableJobAccounts" :key="s.ShipToSequenceId"
                                    @click="selectJobAccount(s)"
                                    class="flex justify-between items-center px-3 py-2.5 text-sm transition-colors duration-200 hover:bg-[#F8F8F8] cursor-pointer">
                                    <span class="truncate w-full text-[#464646]">#{{ s.ShipToSequenceId }}: {{
                                        s.AddressLine2 }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Homeowner Info Section -->
        <div class="border border-[#D1D1D1] rounded-lg p-3">
            <h3 class="font-semibold text-[20px] text-[#171717] mb-4">{{ t('propertyReport.form.homeownerInfo') }}</h3>
            <AddressLookup ref="addressLookupRef" :compact-map="compactMap"
                @address-validity-changed="onAddressValidity" @address-changed="onAddressChange" @search-triggered="onSearchTriggered" />
            <slot name="extended"></slot>
        </div>

        <!-- Property Report Already Exists Dialog -->
        <div v-if="showExistsDialog"
            class="fixed left-0 top-0 w-full h-full flex justify-center items-center bg-[rgba(0,0,0,0.4)] z-[10001] p-2">
            <div class="bg-[#fff] rounded px-3 md:!px-10 py-4 max-w-lg w-full shadow-xl flex flex-col items-center text-center relative">
                <div class="absolute top-2 right-4 cursor-pointer" @click="onExistsCancel">
                    <i class="ri-close-line text-xl text-[#000]"></i>
                </div>
                <i class="ri-alert-fill text-[#762123] text-3xl mb-2"></i>
                <div class="flex items-center gap-2 mb-3">
                    <h3 class="text-[26px] font-semibold text-[#464646]">{{ t('propertyReport.exists.title') }}</h3>
                </div>

                <p class="text-base text-[#000] mb-6">
                    {{ t('propertyReport.exists.description', { date: formatExistsReportDate }) }}
                </p>

                <p class="text-base text-[#000] mb-6">
                    {{ t('propertyReport.exists.continueQuestion') }}
                </p>

                <div class="flex flex-col-reverse md:flex-row w-full md:w-auto">
                    <button @click="onExistsCancel"
                        class="px-4 py-2 text-base text-[#171717] focus:outline-none">
                        {{ t('propertyReport.exists.cancel') }}
                    </button>
                    <button @click="onExistsContinue"
                        class="px-3 py-2 text-base bg-[#762123] text-[#fff] rounded focus:outline-none">
                        {{ t('propertyReport.exists.continue') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Restricted State Notification -->
        <div v-if="isRestrictedState" class="flex items-start gap-2 p-2 bg-[#FEE2E2] border border-[#FCA5A5] rounded">
            <i class="ri-error-warning-line text-[#AF1E2C] text-lg flex-shrink-0 mt-0.5"></i>
            <span class="text-sm text-[#991B1B] font-normal">
                {{ t('propertyReport.form.restrictedStateError') }}
            </span>
        </div>

        <div v-if="!validAddress && addressTouched && !ownerValidationLoading && !isRestrictedState"
            class="flex items-center gap-2 p-2 bg-[#FEE2E2] border border-[#FCA5A5] rounded">
            <i class="ri-error-warning-line text-[#AF1E2C] text-lg flex-shrink-0"></i>
            <span class="text-sm text-[#991B1B] font-normal">
                {{ t('propertyReport.form.invalidAddressError') }}
            </span>
        </div>

        <div v-if="!validOwner && validAddress && addressTouched && !ownerValidationLoading && ownerValidationError && !isRestrictedState"
            class="flex items-center gap-2 p-2 bg-[#FEE2E2] border border-[#FCA5A5] rounded">
            <i class="ri-error-warning-line text-[#AF1E2C] text-lg flex-shrink-0"></i>
            <span class="text-sm text-[#991B1B] font-normal">
                {{ ownerValidationError }}
            </span>
        </div>

        <!-- Primary action row -->
        <div v-if="showButtons" class="flex justify-end gap-3 pt-2">
            <button type="button" class="text-base px-4 py-2 text-[#171717] focus:outline-none bg-[#fff]"
                @click="emitCancel">{{
                    t('propertyReport.form.cancel') }}</button>
            <button :disabled="!validAddress || !validOwner || ownerValidationLoading || isRestrictedState"
                @click="onNext" class="px-3 py-2 text-base rounded text-[#fff] focus:outline-none"
                :class="(validAddress && validOwner && !ownerValidationLoading && !isRestrictedState) ? 'bg-[#762123] hover:bg-[#762123]' : '!text-[#6E6E6E] bg-[#D1D1D1] cursor-not-allowed'">
                {{ t('propertyReport.form.next') }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import AddressLookup from './AddressLookup.vue'
import NewPropertyReportFormBranchSelect from './NewPropertyReportFormBranchSelect.vue'
import { useUserStore } from '@/stores/userStore'
import { usePropertyReportFormStore } from '@/stores/propertyReportFormStore'
import propertyReportsService from '@/services/propertyReportsService'
import { getLanguage } from '@/utilities/cookies'
import Loader from './atoms/Loader.vue'

const { t, locale } = useI18n()
locale.value = getLanguage()

const emit = defineEmits(['cancel', 'next', 'addressChanged'])
const props = defineProps({
    compactMap: { type: Boolean, default: false },
    showButtons: { type: Boolean, default: true }
})

const RESTRICTED_STATES = ['CA', 'OR', 'VT']

const addressLookupRef = ref(null)
const userStore = useUserStore()
const formStore = usePropertyReportFormStore()

// Dropdown states
const isJobAccountDropdownOpen = ref(false)
const isBranchDropdownOpen = ref(false)

function toggleJobAccountDropdown() {
    isJobAccountDropdownOpen.value = !isJobAccountDropdownOpen.value
    if (isJobAccountDropdownOpen.value) {
        isBranchDropdownOpen.value = false
    }
}

function selectJobAccount(account) {
    formStore.setSelectedJobAccount(account.ShipToSequenceId)
    isJobAccountDropdownOpen.value = false
}

function onBranchChange(branchCode) {
    formStore.setSelectedBranch(branchCode)
}

function onBranchDropdownToggle() {
    isJobAccountDropdownOpen.value = false
}

// Address & Owner Validation
const validAddress = ref(false)
const validOwner = ref(false)
const addressTouched = ref(false)
const addressObj = ref({})
const ownerValidationLoading = ref(false)
const ownerValidationError = ref('')
const isRestrictedState = ref(false)

// Property Report Existence Check
const showExistsDialog = ref(false)
const existsReportId = ref(null)
const existsReportDate = ref('')

// Owner data from validation
const ownerData = ref(null)

async function onAddressValidity(e) {
    console.log('onAddressValidity called:', e)

    const stateCode = e.address?.state || ''
    const isStateRestricted = RESTRICTED_STATES.includes(stateCode)

    validAddress.value = e.isValid
    addressObj.value = e.address
    addressTouched.value = true
    isRestrictedState.value = isStateRestricted

    validOwner.value = false
    ownerValidationError.value = ''

    if (isRestrictedState.value) {
        return
    }

    if (e.isValid && addressObj.value.streetAddress) {
        const exists = await checkExistingReports()
        if (exists) {
            showExistsDialog.value = true
            return
        }
        validateOwnerInformation()
    }
}

function onSearchTriggered() {
    emit('addressChanged')
}

function onAddressChange(e) {
    const stateCode = e.address?.state || ''
    const isStateRestricted = RESTRICTED_STATES.includes(stateCode)

    addressObj.value = e.address
    isRestrictedState.value = isStateRestricted
    validOwner.value = false
    ownerValidationError.value = ''
    ownerData.value = null
    showExistsDialog.value = false
    
    if (e.address?.streetAddress) {
        emit('addressChanged')
    }
}

async function validateOwnerInformation() {
    if (!addressObj.value.streetAddress) {
        validOwner.value = false
        ownerValidationError.value = t('propertyReport.form.invalidAddressError')
        return
    }

    ownerValidationLoading.value = true
    ownerValidationError.value = ''

    try {
        const payload = {
            StreetNumber: addressObj.value.streetNumber,
            StreetAddress: addressObj.value.streetAddress,
            City: addressObj.value.city,
            State: addressObj.value.state,
            Zip: addressObj.value.zipCode
        }

        const response = await propertyReportsService.lookupOwner(payload)

        if (response.success) {
            const data = response.data
            ownerData.value = data

            if ((data.PrimaryOwnerFirstName && data.PrimaryOwnerFirstName.trim()) || (data.PrimaryOwnerLastName && data.PrimaryOwnerLastName.trim())) {
                validOwner.value = true
                ownerValidationError.value = ''
            } else {
                validOwner.value = false
                ownerValidationError.value = t('propertyReport.form.invalidOwnerError')
            }
        } else {
            ownerData.value = null
            validOwner.value = false
            ownerValidationError.value = t('propertyReport.form.invalidOwnerError')
        }
    } catch (error) {
        ownerData.value = null
        validOwner.value = false
        ownerValidationError.value = t('propertyReport.form.invalidOwnerError')
    } finally {
        ownerValidationLoading.value = false
    }
}

async function checkExistingReports() {
    if (!addressObj.value.streetAddress) return false

    const searchText = `${addressObj.value.streetNumber},${addressObj.value.streetAddress},${addressObj.value.city},${addressObj.value.state},${addressObj.value.zipCode}`

    const listRequest = {
        CorrelationId: 'c6b9526b-3903-4fd3-bd54-ca705fa0e8e3',
        SearchText: searchText,
        SortOrder: 'Created',
        SortDir: 'DESC',
        PageNumber: 1,
        PageSize: 1
    }

    try {
        const resp = await propertyReportsService.list(listRequest)
        if (resp.success && resp.data?.Reports) {
            const propertyReports = resp.data.Reports.filter(report => report.Status.toLowerCase() !== 'error')

            if (propertyReports.length > 0) {
                const report = propertyReports[0]
                existsReportId.value = report.Status.toLowerCase() === 'completed' ? report.ReportId : '-2'
                existsReportDate.value = report.Created
                return true
            }
        }
    } catch (error) {
        console.error('Error checking existing reports:', error)
    }

    return false
}

const formatExistsReportDate = computed(() => {
    if (!existsReportDate.value) return ''
    return new Date(existsReportDate.value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
})

function onExistsContinue() {
    showExistsDialog.value = false
    validateOwnerInformation()
}

function onExistsCancel() {
    showExistsDialog.value = false
    if (addressLookupRef.value?.clearAddress) {
        addressLookupRef.value.clearAddress()
    }

    validAddress.value = false
    validOwner.value = false
    addressTouched.value = false
    ownerValidationError.value = ''

    addressObj.value = {
        streetNumber: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
    }

    existsReportId.value = null
    existsReportDate.value = ''
}

function onNext() {
    if (!validAddress.value || !validOwner.value) return

    emit('next', {
        addressObj: addressObj.value,
        ownerData: ownerData.value,
        selectedBranchCode: formStore.selectedBranchCode,
        selectedShipToSequence: formStore.selectedShipToSequence,
        selectedJobAccount: formStore.selectedJobAccount,
        pricePerReport: formStore.pricePerReport,
        uomReport: formStore.uomReport,
        freeReportsRemaining: formStore.freeReportsRemaining,
        currentUser: formStore.currentUser
    })
}

function emitCancel() {
    emit('cancel')
}

function handleJobAccountClickOutside(e) {
    if (!e.target.closest('.job-account-select-comp')) {
        isJobAccountDropdownOpen.value = false
    }
}

// Expose method to clear address (for parent to call on success modal close)
function clearAddress() {
    if (addressLookupRef.value?.clearAddress) {
        addressLookupRef.value.clearAddress()
    }
    validAddress.value = false
    validOwner.value = false
    addressTouched.value = false
    ownerValidationError.value = ''
    isRestrictedState.value = false
    showExistsDialog.value = false
    existsReportId.value = null
    existsReportDate.value = ''
    ownerData.value = null
    addressObj.value = {
        streetNumber: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
    }
}

defineExpose({ clearAddress })

onMounted(async () => {
    try {
        await formStore.initialize()
        document.addEventListener('mousedown', handleJobAccountClickOutside)
    } catch (error) {
        console.error('Error initializing form:', error)
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleJobAccountClickOutside)
})
</script>
